import { getCompareCarlines } from '@oneaudi/feature-app-utils/compareUtils';
import type { Dispatch, PropsWithChildren } from 'react';
import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import type { Action } from './compareContextReducer';
import { getNumberOfCars, reducer } from './compareContextReducer';
import { useContent } from './ContentContext';
import { DataContext } from './DataContext';
import { useCompareTrackingManager } from '../hooks/useCompareTrackingManager';

export type CompareCarline = {
  readonly id: string;
  readonly name: string;
  readonly group: string;
};

export type State = { readonly amount: 2 | 3; readonly carlines: CompareCarline[] };

const getMobileMatcher = (): MediaQueryList | undefined =>
  typeof window !== 'undefined' ? window.matchMedia('(max-width: 1023px)') : undefined;

const createInitialState = (): State => ({
  amount: getNumberOfCars(getMobileMatcher()?.matches ?? true),
  carlines: [],
});

export type CompareContextValue = { readonly state: State; readonly dispatch: Dispatch<Action> };

const disabledContextValue: CompareContextValue = {
  state: createInitialState(),
  dispatch: () => {},
};

export const CompareContext = createContext<CompareContextValue>({
  state: createInitialState(),
  dispatch: () => {},
});

export const CompareContextProvider = ({ children }: PropsWithChildren) => {
  const { compareMode } = useContent();
  const { allCarlineGroups } = useContext(DataContext);
  const [state, dispatch] = useReducer(reducer, createInitialState());
  const value = useMemo<CompareContextValue>(
    () => (compareMode === 'off' ? disabledContextValue : { state, dispatch }),
    [compareMode, state],
  );
  const { autoRemove: trackAutoRemove } = useCompareTrackingManager();

  useEffect(() => {
    if (compareMode !== 'off' && allCarlineGroups.length > 0) {
      const carlines = getCompareCarlines(window.location);
      if (carlines) {
        dispatch({
          type: 'init',
          carlines,
          allCarlineGroups,
        });
      }
    }
  }, [compareMode, allCarlineGroups]);

  useEffect(() => {
    let matcher: MediaQueryList | undefined;
    const changeHandler = (e: MediaQueryListEvent) => {
      dispatch({ type: 'set-cars', amount: getNumberOfCars(e.matches), trackAutoRemove });
    };
    if (compareMode !== 'off') {
      matcher = getMobileMatcher();
      matcher?.addEventListener('change', changeHandler);
    }

    return () => {
      matcher?.removeEventListener('change', changeHandler);
    };
  }, [compareMode]);

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
};
